import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useWebCall } from '../../hooks';
import { MeetingState, PracticeProspect } from '../../types';
import DialerContent from './DialerContent';
import { NextProspectDetails } from './DialerEndActions/UpNext';
import DialerWrapper from './DialerWrapper';

interface WebSocketDialerProps {
  currProspect: PracticeProspect;
  callNextProspect: () => void;
  closeDialer: () => void;
  setProspects: Dispatch<SetStateAction<PracticeProspect[]>>;
  nextProspectDetails?: NextProspectDetails;
}

const WebSocketDialer = ({
  currProspect,
  callNextProspect,
  closeDialer,
  setProspects,
  nextProspectDetails,
}: WebSocketDialerProps) => {
  // Initially true to show the loading spinner when the modal opens until the call starts.
  const [isLoading, setIsLoading] = useState(true);
  const [isMicAccessGranted, setIsMicAccessGranted] = useState(false);
  const [isRedialing, setIsRedialing] = useState(false);

  const { callSid, meetingState, startCall, stopCall, ...webCallProps } = useWebCall(closeDialer);

  const personaId = currProspect.personaId;

  const runStartCall = useCallback(
    (newIsRedialing: boolean) => {
      if (isMicAccessGranted) {
        setIsRedialing(newIsRedialing);
        startCall(personaId);
      }
    },
    // We purposely don't include the startCall function in the dependency array because it will cause a loop.
    [isMicAccessGranted, personaId]
  );

  useEffect(() => {
    if (isLoading && meetingState !== MeetingState.IDLE && !isRedialing) {
      setIsLoading(meetingState === MeetingState.CONNECTING_TO_MIC);
    } else if (meetingState === MeetingState.IDLE && isRedialing) {
      // Reset the redialing state once the call is idle.
      setIsRedialing(false);
    }
  }, [isLoading, meetingState, isRedialing]);

  return (
    <DialerWrapper
      callSid={callSid}
      currProspect={currProspect}
      isLoading={isLoading}
      isMicAccessGranted={isMicAccessGranted}
      meetingState={meetingState}
      handleClose={closeDialer}
      runStartCall={runStartCall}
      setIsMicAccessGranted={setIsMicAccessGranted}
      setProspects={setProspects}
      stopCall={stopCall}
    >
      <DialerContent
        callSid={callSid}
        currProspect={currProspect}
        isDaily={false}
        isRedialing={isRedialing}
        meetingState={meetingState}
        nextProspectDetails={nextProspectDetails}
        callNextProspect={callNextProspect}
        closeDialer={closeDialer}
        redial={() => runStartCall(true)}
        setProspects={setProspects}
        stopCall={stopCall}
        {...webCallProps}
      />
    </DialerWrapper>
  );
};

export default WebSocketDialer;
